// react
import { useContext, useMemo, useState } from 'react';

// types
import {
	Contract,
	Document,
	DocumentType,
	Entity,
	EntityType,
	Frame,
	Pagination,
} from '../../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// hooks
import { useFetch } from '../../../../hooks/useFetch';

// context
import { ContextProject } from '../../../pages/Project/Project.context';
import { ContextUser, ContextUserEntity } from '../../../../App.context';

// mui
import { Grid } from '@mui/material';

// ui
import { BtnFilters } from '../../../ui/BtnFilters/BtnFilters';
import { EmptyProps } from '../../../ui/Empty/Empty';
import { FilterChangeEvent, FilterProps } from '../../../ui/Filters/Filters';
import { TableHeaderProps } from '../../../ui/TableHeaders/TableHeader/TableHeader';
import { TableHeaders } from '../../../ui/TableHeaders/TableHeaders';
import { TableFooter } from '../../../ui/TableFooter/TableFooter';

// utils
import { translateType } from '../../../../utils/translations';

// cards
import { CardDocument } from '../../../cards/CardDocument/CardDocument';
import { CardEmpty } from '../../../cards/CardEmpty/CardEmpty';

// forms
import { FormSearch } from '../../../forms/FormSearch/FormSearch';

// icons
import { InsertDriveFileOutlined as IconInsertDriveFile } from '@mui/icons-material';

// props
export interface TableDocumentsProps {
	documents: Document[];
	entityType?: EntityType;
	frame?: Frame;
	isLoading: boolean;
	pagination: Pagination;
	refetchDocuments?: () => void;
}

export const TableDocuments = ({
	documents,
	entityType,
	frame = 'PROJECT',
	isLoading,
	pagination,
	refetchDocuments,
}: TableDocumentsProps) => {
	// hooks
	const [searchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramContract = searchParams.get('contract');
	const paramEntity = searchParams.get('entity');
	const paramQ = searchParams.get('q');
	const paramType = searchParams.get('type');

	// context
	const { project } = useContext(ContextProject);
	const { user } = useContext(ContextUser);
	const { userEntity } = useContext(ContextUserEntity);

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// defaults
	const defaultFiltersValues = {
		contract: 'all',
		entity: 'all',
		type: 'all',
	};

	// state
	const [contracts, setContracts] = useState<Contract[]>([]);
	const [docTypes, setDocTypes] = useState<DocumentType[]>([]);
	const [entities, setEntities] = useState<Entity[]>([]);
	const [filtersValues, setFiltersValues] = useState({
		contract: paramContract || defaultFiltersValues.contract,
		entity: paramEntity || defaultFiltersValues.entity,
		type: paramType || defaultFiltersValues.type,
	});

	// vars
	const hasColumnContract = entityType !== 'BENEFICIARY';
	const hasColumnEntity = ['BENEFICIARIES', 'SUPPLIERS'].includes(frame) && entityType;
	const isTypeSupplier = entityType === 'SUPPLIER';

	// fetch doc types
	let urlParamEntity = 'null';

	if (userEntity) {
		urlParamEntity += `,${userEntity.id}`;
	}

	// fetch document types
	useFetch({
		isEnabled: Boolean(user),
		url: `${process.env.REACT_APP_API_URL}/document-types?entity=${urlParamEntity}`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setDocTypes(res.data);
			}
		},
	});

	// fetch entities
	let fetchEntitiesUrl = `${process.env.REACT_APP_API_URL}/entities?project=${project?.id}&fields=id,name`;

	if (entityType) {
		fetchEntitiesUrl += `&type=${entityType}`;
	}

	useFetch({
		isEnabled: Boolean(project?.id),
		url: fetchEntitiesUrl,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setEntities(res.data);
			}
		},
	});

	// fetch contracts
	useFetch({
		isEnabled: Boolean(isTypeSupplier && project?.id),
		url: `${process.env.REACT_APP_API_URL}/contracts?project=${project?.id}&fields=id,num`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setContracts(res.data);
			}
		},
	});

	// options
	const optionsContracts = contracts.map((contract) => {
		return {
			label: contract.num,
			value: String(contract.id),
		};
	});

	const optionsEntities = entities.map((entity) => {
		return {
			label: entity.name,
			value: String(entity.id),
		};
	});

	const optionsType = docTypes.map((docType) => {
		return {
			label: docType.name,
			value: String(docType.id),
		};
	});

	// headers
	const headers: TableHeaderProps[] = [
		{
			GridProps: {
				lg: 2,
			},
			isSortable: true,
			label: t('file'),
			value: 'name',
		},
		{
			GridProps: {
				lg: frame === 'BENEFICIARY' ? 3 : 1.5,
			},
			label: t('type'),
			value: 'type',
		},
	];

	if (hasColumnEntity) {
		headers.push({
			GridProps: {
				lg: 2,
			},
			label: translateType(entityType, t),
			value: 'entity.type',
		});
	}

	if (hasColumnContract) {
		headers.push({
			GridProps: {
				lg: 1.5,
			},
			label: t('contract'),
			value: 'contract',
		});
	}

	headers.push(
		{
			GridProps: {
				lg: hasColumnEntity ? 1.5 : 2,
			},
			label: t('transaction'),
			value: 'transaction',
		},
		{
			GridProps: {
				lg: 1.5,
			},
			label: t('created'),
			value: 'created',
		},
		{
			GridProps: {
				lg: 1.5,
			},
			label: t('hash'),
			value: 'notarizationHash',
		}
	);

	// handlers
	const handleChangeFilterValue = (e: FilterChangeEvent) => {
		const { name, value } = e.target as HTMLInputElement;
		setFiltersValues({
			...filtersValues,
			[name]: value,
		});
	};

	const idFormFilters = 'form-documents-filters';

	// filters
	const filters: FilterProps[] = [
		{
			hasAllOption: true,
			hasNoneOption: false,
			hasSearchParam: true,
			id: `${idFormFilters}-type`,
			label: t('type'),
			name: 'type',
			onChange: handleChangeFilterValue,
			options: optionsType,
			type: 'SELECT',
			value: filtersValues.type,
		},
	];

	if (isTypeSupplier) {
		filters.push({
			hasAllOption: true,
			hasNoneOption: false,
			hasSearchParam: true,
			id: `${idFormFilters}-contract`,
			label: t('contract'),
			name: 'contract',
			onChange: handleChangeFilterValue,
			options: optionsContracts,
			type: 'SELECT',
			value: filtersValues.contract,
		});
	}

	if (frame === 'PROJECT' && entityType) {
		filters.push({
			hasAllOption: true,
			hasNoneOption: false,
			hasSearchParam: true,
			id: `${idFormFilters}-entity`,
			label: translateType(entityType, t),
			name: 'entity',
			onChange: handleChangeFilterValue,
			options: optionsEntities,
			type: 'SELECT',
			value: filtersValues.entity,
		});
	}

	// memo
	const empty = useMemo(() => {
		const emptyProps: EmptyProps = {
			content: { children: t('empty.documents.content') },
			IconCircleProps: { icon: <IconInsertDriveFile /> },
			title: { children: t('empty.documents.title') },
		};

		if (paramQ) {
			emptyProps.content = { children: t('empty.default.content') };
			emptyProps.IconCircleProps = { icon: null };
		}

		return emptyProps;
	}, [paramQ, t]);

	const content = useMemo(() => {
		if (isLoading || !documents.length) {
			return <CardEmpty empty={empty} isLoading={isLoading} />;
		}

		return (
			<Grid container spacing={2}>
				{documents.map((document, i) => {
					return (
						<Grid item key={`document-${i}`} xs={12}>
							<CardDocument
								document={document}
								entityType={entityType}
								frame={frame}
								refetchDocuments={refetchDocuments}
							/>
						</Grid>
					);
				})}
			</Grid>
		);
	}, [empty, frame, isLoading, documents, entityType, refetchDocuments]);

	return (
		<Grid container spacing={2}>
			<Grid item xs={12}>
				<Grid container spacing={2}>
					<Grid item xs={true}>
						<FormSearch InputProps={{ placeholder: t('searchByIdOrName') }} />
					</Grid>
					<Grid item container xs={'auto'}>
						<BtnFilters
							filters={filters}
							onReset={() => {
								setFiltersValues(defaultFiltersValues);
							}}
						/>
					</Grid>
				</Grid>
			</Grid>
			<Grid item xs={12} sx={{ display: { xs: 'none', lg: 'block' } }}>
				<TableHeaders headers={headers} />
			</Grid>
			<Grid item xs={12}>
				{content}
			</Grid>
			<Grid item xs={12}>
				<TableFooter numShowing={documents.length} pagination={pagination} />
			</Grid>
		</Grid>
	);
};
